import { Injectable } from "@angular/core";
import { AUTH_CONSTANTS } from "../components/base-auth/base-auth.component";
import { AuthService } from "@nts/std";
import { LocalstorageHelper, SerializabledDataType } from "@nts/std/src/lib/utility";

@Injectable()
export class LoginAuthService extends AuthService {

  /**
   * Override del metodo per pulire i dati di autenticazione
   * In questo modo gestisco la pulizia del token in sessionStorage o localStorage a seconda del flag "Remember Me"
   */
  override async clearAuthData(): Promise<void> {
    await super.clearAuthData();
    window.sessionStorage.removeItem(AuthService.ACCESS_TOKEN_KEY);
    window.sessionStorage.removeItem(AuthService.REFRESH_TOKEN_KEY);
  }

  /**
   * Controlla se il flag "Remember Me" è settato
   * @returns {Promise<boolean>} True se il flag "Remember Me" è settato, false altrimenti
   */
  async isRememberMeSet(): Promise<boolean> {
    return (await LocalstorageHelper.getStorageItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME, undefined, false, false)) === 'true';
  }

  /**
   * Recupera l'access token
   * In questo modo gestisco il recupero del token in sessionStorage o localStorage a seconda del flag "Remember Me"
   * @returns access token
   */
  protected override async retrieveAccessToken(): Promise<SerializabledDataType> {
    if (await this.isRememberMeSet()) {
      return await LocalstorageHelper.getStorageItem(AuthService.ACCESS_TOKEN_KEY, undefined, false, false);
    } else {
      return window.sessionStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
    }
  }

  /**
   * Salva un nuovo access token
   * In questo modo gestisco il salvataggio del token in sessionStorage o localStorage a seconda del flag "Remember Me"
   * @param newAccessToken nuovo access token da salvare
   * @returns true se il salvataggio è andato a buon fine, false altrimenti
   */
  protected override async storeAccessToken(newAccessToken: string): Promise<boolean> {
    if (await this.isRememberMeSet()) {
      return await LocalstorageHelper.setStorageItem(AuthService.ACCESS_TOKEN_KEY, newAccessToken, undefined, false, false);
    } else {
      window.sessionStorage.setItem(AuthService.ACCESS_TOKEN_KEY, newAccessToken);
      return true;
    }
  }

  /**
   * Recupera il token di refresh
   * In questo modo gestisco il recupero del token in sessionStorage o localStorage a seconda del flag "Remember Me"
   * @returns refresh token
   */
  protected override async retrieveRefreshToken(): Promise<SerializabledDataType> {
    if (await this.isRememberMeSet()) {
      return await LocalstorageHelper.getStorageItem(AuthService.REFRESH_TOKEN_KEY, undefined, false, false);
    } else {
      return window.sessionStorage.getItem(AuthService.REFRESH_TOKEN_KEY);
    }
  }

  /**
   * Salva il token di refresh
   * In questo modo gestisco il salvataggio del token in sessionStorage o localStorage a seconda del flag "Remember Me"
   * @param newRefreshToken nuovo refresh token da salvare
   * @returns true se il salvataggio è andato a buon fine, false altrimenti
   */
  protected override async storeRefreshToken(newRefreshToken: string): Promise<boolean> {
    if (await this.isRememberMeSet()) {
      return await LocalstorageHelper.setStorageItem(AuthService.REFRESH_TOKEN_KEY, newRefreshToken, undefined, false, false);
    } else {
      window.sessionStorage.setItem(AuthService.REFRESH_TOKEN_KEY, newRefreshToken);
      return true;
    }
  }

  /**
   * Salva il profilo tenant
   * In questo modo gestisco il salvataggio del profilo in sessionStorage o localStorage a seconda del flag "Remember Me"
   * @param tenantProfile oggetto plain js contenente le proprietà del profilo tenant
   */
  protected override async storeTenantProfile(tenantProfile: Record<string, any>): Promise<boolean> {
    if (await this.isRememberMeSet()) {
      return await LocalstorageHelper.setStorageItem(AuthService.TENANT_PROFILE, tenantProfile, undefined, undefined, false);
    } else {
      window.sessionStorage.setItem(AuthService.TENANT_PROFILE, JSON.stringify(tenantProfile));
      return true;
    }
  }

  /**
   * Recupera il profilo tenant
   * In questo modo gestisco il recupero del profilo in sessionStorage o localStorage a seconda del flag "Remember Me"
   * @returns oggetto plain js contenente le proprietà del profilo tenant
   */
  protected override async retrieveTenantProfile(): Promise<Record<string, any>> {
    if (await this.isRememberMeSet()) {
      return await LocalstorageHelper.getStorageItem(AuthService.TENANT_PROFILE, undefined, undefined, false) as Record<string, any>;
    } else {
      try {
        return JSON.parse(window.sessionStorage.getItem(AuthService.TENANT_PROFILE) || '{}')
      } catch (e) {
        return JSON.parse('{}')
      }
    }
  }

  /**
     * Salva il profilo utente
     * In questo modo gestisco il salvataggio del profilo in sessionStorage o localStorage a seconda del flag "Remember Me"
     * @param userProfile oggetto plain js contenente le proprietà del profilo utente
     */
  protected override async storeUserProfile(userProfile: Record<string, any>) {
    if (await this.isRememberMeSet()) {
      await LocalstorageHelper.setStorageItem(AuthService.USER_PROFILE, userProfile, undefined, false, false);
    } else {
      window.sessionStorage.setItem(AuthService.USER_PROFILE, JSON.stringify(userProfile));
    }
  }

  /**
   * Recupera il profilo utente
   * In questo modo gestisco il recupero del profilo in sessionStorage o localStorage a seconda del flag "Remember Me"
   * @returns oggetto plain js contenente le proprietà del profilo utente
   */
  protected override async retrieveUserProfile(): Promise<Record<string, any>> {
    let plainUser: any;
    if (await this.isRememberMeSet()) {
      plainUser = await LocalstorageHelper.getStorageItem(AuthService.USER_PROFILE, undefined, false, false);
    } else {
      try {
        plainUser = JSON.parse(window.sessionStorage.getItem(AuthService.USER_PROFILE) || '{}')
      } catch (e) {
        plainUser = JSON.parse('{}')
      }
    }
    return plainUser;
  }
}
